/**
 * MenuImportModal.js
 * Bulk menu upload for the Menu Management drawer.
 *
 * Flow: pick the workbook and the photo files, validate (nothing is written),
 * read the per-row report, then import. The two steps are deliberate: `image`
 * is required on every RestaurantProduct, so a row that cannot resolve a photo
 * would otherwise leave a half-built menu behind.
 *
 * Parsing and sequencing live in ../../utilities/menuImport.js and
 * ../../utilities/menuImportRunner.js so they can be tested without React.
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import { Modal, Button, Spinner, Alert, Table, Badge, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaFileExcel,
  FaImages,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaUpload,
  FaDownload,
} from "react-icons/fa";

import { buildImportPlan } from "../../utilities/menuImport";
import { runMenuImport } from "../../utilities/menuImportRunner";
import { uploadImageToCloudinary } from "../../utilities/imageUpload";
import { PRODUCT_PHOTO_RULE_TEXT } from "../../utilities/productPhotoRules";
import {
  apiAdminCreateSection,
  apiFetchRestaurantSections,
  apiCreateRestaurantProduct,
  apiUpdateRestaurantProduct,
  apiCreateRestaurantOption,
  apiRestaurantCreateOptionGroup,
} from "../../api/index";

const STEP = { PICK: "pick", REVIEW: "review", RUNNING: "running", DONE: "done" };

/** Blank workbook served from public/templates — always available to download. */
const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/templates/Lusuku_Menu_Import_Template.xlsx`;

/**
 * Theme-safe progress bar. The admin theme slims Bootstrap's .progress track,
 * so ProgressBar's in-bar label gets clipped into an illegible smear — the
 * count and phase live OUTSIDE the track instead.
 */
const ImportProgress = ({ done, total, phase, label }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  const phaseText =
    phase === "sections"
      ? "Creating sections"
      : phase === "options"
      ? "Creating option groups"
      : phase === "products"
      ? "Uploading photos and creating products"
      : "Preparing";
  return (
    <div style={{ padding: "26px 0" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.8rem",
          fontWeight: 700,
          color: "#4a5c6e",
          marginBottom: 6,
        }}
      >
        <span>{phaseText}</span>
        <span>
          {done} of {total} · {pct}%
        </span>
      </div>
      <div
        style={{
          height: 12,
          background: "#e6ebf1",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: "linear-gradient(90deg, #2c6fbb, #1e9e63)",
            borderRadius: 8,
            transition: "width 0.35s ease",
          }}
        />
      </div>
      <p
        style={{
          marginTop: 14,
          textAlign: "center",
          color: "#6b7c93",
          fontSize: "0.86rem",
          minHeight: 20,
        }}
      >
        {label || ""}
      </p>
      <p style={{ textAlign: "center", color: "#9aacbf", fontSize: "0.78rem", marginBottom: 0 }}>
        Leave this open until it finishes.
      </p>
    </div>
  );
};

const pill = (tone) => ({
  fontSize: "0.7rem",
  fontWeight: 700,
  padding: "3px 8px",
  borderRadius: 20,
  background:
    tone === "ok" ? "#e6f4ea" : tone === "warn" ? "#fff4e5" : "#fdecea",
  color: tone === "ok" ? "#1e7e34" : tone === "warn" ? "#a35b00" : "#b3261e",
});

const MenuImportModal = ({ show, restaurant, existingSections, onClose, onImported }) => {
  const [step, setStep] = useState(STEP.PICK);
  const [workbook, setWorkbook] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [plan, setPlan] = useState(null);
  const [parseError, setParseError] = useState("");
  const [validating, setValidating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, label: "", phase: "" });
  const [result, setResult] = useState(null);
  // Rows that match a dish already on the menu (same section, same name) are
  // updated in place by default. Unticked, they are left untouched. They are
  // never duplicated either way.
  const [updateExisting, setUpdateExisting] = useState(true);
  // New dishes land deactivated by default so QC can check them before
  // customers see them (same switch as the brand importer).
  const [importDisabled, setImportDisabled] = useState(true);

  const workbookRef = useRef(null);
  const photosRef = useRef(null);

  const reset = useCallback(() => {
    setStep(STEP.PICK);
    setWorkbook(null);
    setPhotos([]);
    setPlan(null);
    setParseError("");
    setValidating(false);
    setProgress({ done: 0, total: 0, label: "", phase: "" });
    setResult(null);
    setUpdateExisting(true);
    if (workbookRef.current) workbookRef.current.value = "";
    if (photosRef.current) photosRef.current.value = "";
  }, []);

  const handleClose = useCallback(() => {
    if (step === STEP.RUNNING) return; // never close mid-write
    const imported =
      result &&
      ((result.created && result.created.products > 0) ||
        (result.updated && result.updated.products > 0));
    reset();
    onClose();
    if (imported && onImported) onImported();
  }, [step, result, reset, onClose, onImported]);

  const handleValidate = useCallback(async () => {
    if (!workbook) {
      toast.error("Choose the filled workbook first");
      return;
    }
    setValidating(true);
    setParseError("");
    try {
      const built = await buildImportPlan(workbook, photos, existingSections || []);
      setPlan(built);
      setStep(STEP.REVIEW);
    } catch (err) {
      setParseError(err.message || "That workbook could not be read.");
    } finally {
      setValidating(false);
    }
  }, [workbook, photos, existingSections]);

  const handleRun = useCallback(async () => {
    if (!plan || !plan.canCommit) return;
    setStep(STEP.RUNNING);
    try {
      const res = await runMenuImport(plan, {
        restaurant,
        createSection: apiAdminCreateSection,
        fetchSections: apiFetchRestaurantSections,
        createOption: apiCreateRestaurantOption,
        createOptionGroup: apiRestaurantCreateOptionGroup,
        createProduct: apiCreateRestaurantProduct,
        updateProduct: apiUpdateRestaurantProduct,
        updateExisting,
        forceDisabled: importDisabled,
        uploadImage: uploadImageToCloudinary,
        onProgress: setProgress,
      });
      setResult(res);
      setStep(STEP.DONE);
      const madeNew = res.created.products;
      const changed = res.updated ? res.updated.products : 0;
      if (madeNew > 0 || changed > 0) {
        toast.success(
          [
            madeNew > 0 ? `${madeNew} new menu item${madeNew === 1 ? "" : "s"}` : "",
            changed > 0 ? `${changed} updated` : "",
          ]
            .filter(Boolean)
            .join(", ")
        );
      } else if (res.skipped && res.skipped.products > 0 && res.failed.products === 0) {
        toast.info("Everything in the workbook is already on the menu. Nothing changed.");
      } else {
        toast.error("Nothing was imported. See the log below.");
      }
    } catch (err) {
      setResult({
        created: { sections: 0, optionGroups: 0, products: 0, images: 0 },
        updated: { products: 0 },
        skipped: { products: 0 },
        failed: { sections: 0, optionGroups: 0, products: 0 },
        log: [{ level: "error", message: err.message || "The import stopped unexpectedly." }],
      });
      setStep(STEP.DONE);
    }
  }, [plan, restaurant, updateExisting, importDisabled]);

  const totals = plan ? plan.totals : null;

  const photoSummary = useMemo(() => {
    if (!photos.length) return "No photos chosen yet";
    const mb = photos.reduce((n, f) => n + (f.size || 0), 0) / (1024 * 1024);
    return `${photos.length} photo${photos.length === 1 ? "" : "s"} (${mb.toFixed(1)} MB)`;
  }, [photos]);

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static" centered scrollable>
      <Modal.Header closeButton={step !== STEP.RUNNING}>
        <Modal.Title>
          Import menu {restaurant ? `into ${restaurant.name}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ── Step 1: pick the files ─────────────────────────────────────── */}
        {step === STEP.PICK && (
          <>
            <p style={{ color: "#6b7c93", fontSize: "0.88rem" }}>
              Fill the Lusuku menu workbook, then choose it here along with the
              vendor&apos;s photo files. Nothing is written until you have reviewed the check.
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "#eef4fb",
                border: "1px solid #cfe0f3",
                borderRadius: 8,
                padding: "10px 14px",
              }}
            >
              <FaDownload style={{ color: "#2c6fbb", flexShrink: 0 }} />
              <div style={{ fontSize: "0.84rem", color: "#3d5876" }}>
                Starting fresh?{" "}
                <a
                  href={TEMPLATE_URL}
                  download="Lusuku_Menu_Import_Template.xlsx"
                  style={{ fontWeight: 700, color: "#2c6fbb" }}
                >
                  Download the blank template
                </a>{" "}
                — sections, menu items and option groups with instructions and an example row.
              </div>
            </div>

            <div style={{ display: "grid", gap: 14, marginTop: 18 }}>
              <label style={{ display: "block" }}>
                <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 6 }}>
                  <FaFileExcel style={{ marginRight: 6, color: "#1e7e34" }} />
                  Workbook (.xlsx) <span style={{ color: "#b3261e" }}>*</span>
                </div>
                <input
                  ref={workbookRef}
                  type="file"
                  accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="form-control"
                  onChange={(e) => setWorkbook(e.target.files[0] || null)}
                />
                <small style={{ color: "#9aacbf" }}>
                  Editing in Google Sheets is fine. Use File, Download, Microsoft Excel (.xlsx).
                </small>
              </label>

              <label style={{ display: "block" }}>
                <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 6 }}>
                  <FaImages style={{ marginRight: 6, color: "#2c6fbb" }} />
                  Photo files
                </div>
                <input
                  ref={photosRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="form-control"
                  onChange={(e) => setPhotos(Array.from(e.target.files || []))}
                />
                <small style={{ color: "#9aacbf" }}>
                  Select every photo the sheet names. Matched on image_filename, ignoring case.
                  Each photo: {PRODUCT_PHOTO_RULE_TEXT}. Rows that already carry a
                  cloudinary_url need no file. {photoSummary}
                </small>
              </label>
            </div>

            {parseError && (
              <Alert variant="danger" style={{ marginTop: 16, fontSize: "0.85rem" }}>
                {parseError}
              </Alert>
            )}
          </>
        )}

        {/* ── Step 2: the report ─────────────────────────────────────────── */}
        {step === STEP.REVIEW && plan && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              <span style={pill("ok")}>
                {totals.itemsToCreate} new item{totals.itemsToCreate === 1 ? "" : "s"}
              </span>
              {totals.itemsToUpdate > 0 && (
                <span style={pill(updateExisting ? "ok" : "warn")}>
                  {totals.itemsToUpdate} already on the menu
                  {updateExisting ? " (will be updated)" : " (left untouched)"}
                </span>
              )}
              {totals.itemsFailed > 0 && (
                <span style={pill("err")}>{totals.itemsFailed} cannot import</span>
              )}
              {totals.itemsWithWarnings > 0 && (
                <span style={pill("warn")}>{totals.itemsWithWarnings} with warnings</span>
              )}
              <span style={pill("ok")}>
                {totals.sectionsToCreate} new section{totals.sectionsToCreate === 1 ? "" : "s"}
              </span>
              {totals.sectionsExisting > 0 && (
                <span style={pill("ok")}>{totals.sectionsExisting} section reused</span>
              )}
              <span style={pill("ok")}>{totals.imagesToUpload} images to upload</span>
              {totals.itemsWithoutPhoto > 0 && (
                <span style={pill("warn")}>
                  {totals.itemsWithoutPhoto} without a photo
                </span>
              )}
            </div>

            <p style={{ fontSize: "0.78rem", color: "#9aacbf" }}>
              Read from{" "}
              <strong>{plan.sheetsUsed.items}</strong>
              {plan.sheetsUsed.sections ? `, ${plan.sheetsUsed.sections}` : ""}
              {plan.sheetsUsed.optionGroups ? `, ${plan.sheetsUsed.optionGroups}` : ""}.
              Commission and country are calculated by the server and are never sent from here.
            </p>

            <Form.Check
              type="switch"
              id="menu-import-disabled"
              checked={importDisabled}
              onChange={(e) => setImportDisabled(e.target.checked)}
              style={{ fontSize: "0.84rem", marginBottom: 12, textTransform: "none" }}
              label={
                <span style={{ textTransform: "none" }}>
                  <strong>Import everything deactivated</strong>. New dishes are created
                  hidden from customers and you activate them from the menu drawer, one at
                  a time or a selection at once, after checking names, prices and photos.
                  Leave this on unless the workbook has already been checked.
                </span>
              }
            />

            {totals.itemsToUpdate > 0 && (
              <Form.Check
                type="checkbox"
                id="menu-import-update-existing"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                style={{ fontSize: "0.84rem", marginBottom: 12 }}
                label={
                  <>
                    Update the {totals.itemsToUpdate} dish
                    {totals.itemsToUpdate === 1 ? "" : "es"} that already exist (matched by
                    section and name). Their price, description and other details are taken
                    from the workbook; a row with no photo keeps the dish&apos;s current photos.
                    Untick to leave them exactly as they are. Nothing is duplicated either way.
                  </>
                }
              />
            )}

            {plan.unusedFiles.length > 0 && (
              <Alert variant="warning" style={{ fontSize: "0.8rem", padding: "8px 12px" }}>
                {plan.unusedFiles.length} chosen photo
                {plan.unusedFiles.length === 1 ? " is" : "s are"} not named by any row and will be
                ignored.
              </Alert>
            )}

            <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid #e6ebf1", borderRadius: 6 }}>
              <Table size="sm" hover style={{ marginBottom: 0, fontSize: "0.82rem" }}>
                <thead style={{ position: "sticky", top: 0, background: "#f7f9fb" }}>
                  <tr>
                    <th style={{ width: 44 }}>Row</th>
                    <th>Item</th>
                    <th>Section</th>
                    <th style={{ width: 90 }}>Price</th>
                    <th style={{ width: 92 }}>Photo</th>
                    <th style={{ width: 84 }}>Action</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.items.map((it) => (
                    <tr key={it.sourceRow} style={{ background: it.ok ? undefined : "#fdf3f2" }}>
                      <td style={{ color: "#9aacbf" }}>{it.sourceRow}</td>
                      <td style={{ fontWeight: 600 }}>{it.name || <em>(no name)</em>}</td>
                      <td>{it.sectionTitle}</td>
                      <td>{it.price === null ? "-" : it.price.toLocaleString()}</td>
                      <td>
                        {it.cover.kind === "file" && <Badge bg="secondary">file</Badge>}
                        {it.cover.kind === "url" && <Badge bg="info">url</Badge>}
                        {(it.cover.kind === "missing" || it.cover.kind === "none") &&
                          (it.existing && it.existing.image ? (
                            <Badge bg="light" text="dark">
                              keeps current
                            </Badge>
                          ) : (
                            <Badge bg="warning" text="dark">
                              add later
                            </Badge>
                          ))}
                      </td>
                      <td>
                        {it.action === "update" ? (
                          <Badge bg={updateExisting ? "primary" : "secondary"}>
                            {updateExisting ? "update" : "skip"}
                          </Badge>
                        ) : (
                          <Badge bg="success">new</Badge>
                        )}
                      </td>
                      <td>
                        {it.ok ? (
                          it.warnings.length ? (
                            <span style={{ color: "#a35b00" }}>
                              <FaExclamationTriangle style={{ marginRight: 5 }} />
                              {it.warnings.join("; ")}
                            </span>
                          ) : (
                            <span style={{ color: "#1e7e34" }}>
                              <FaCheckCircle style={{ marginRight: 5 }} />
                              Ready
                            </span>
                          )
                        ) : (
                          <span style={{ color: "#b3261e" }}>
                            <FaTimesCircle style={{ marginRight: 5 }} />
                            {it.errors.join("; ")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {totals.itemsWithoutPhoto > 0 && (
              <Alert variant="info" style={{ marginTop: 12, fontSize: "0.82rem" }}>
                {totals.itemsWithoutPhoto} item
                {totals.itemsWithoutPhoto === 1 ? "" : "s"} will import without a photo. They go
                live with a "no photo yet" tile, and you can add the pictures whenever the shoot
                is done. Find them any time under the menu editor's "No photo" filter.
              </Alert>
            )}

            {totals.itemsFailed > 0 && (
              <Alert variant="warning" style={{ marginTop: 12, fontSize: "0.82rem" }}>
                {totals.itemsFailed} row{totals.itemsFailed === 1 ? "" : "s"} will be skipped.
                Fix them in the workbook and run the import again, or continue and add them by hand
                later. Re-running the same workbook is safe: rows already on the menu are matched
                by section and name and are updated or left alone, never duplicated.
              </Alert>
            )}
          </>
        )}

        {/* ── Step 3: running ────────────────────────────────────────────── */}
        {step === STEP.RUNNING && (
          <ImportProgress
            done={progress.done}
            total={progress.total}
            phase={progress.phase}
            label={progress.label}
          />
        )}

        {/* ── Step 4: outcome ────────────────────────────────────────────── */}
        {step === STEP.DONE && result && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              <span style={pill("ok")}>{result.created.products} new products</span>
              {result.updated && result.updated.products > 0 && (
                <span style={pill("ok")}>{result.updated.products} updated</span>
              )}
              {result.skipped && result.skipped.products > 0 && (
                <span style={pill("warn")}>{result.skipped.products} left untouched</span>
              )}
              <span style={pill("ok")}>{result.created.sections} sections</span>
              <span style={pill("ok")}>{result.created.optionGroups} option groups</span>
              <span style={pill("ok")}>{result.created.images} images uploaded</span>
              {result.failed.products > 0 && (
                <span style={pill("err")}>{result.failed.products} products failed</span>
              )}
            </div>
            {importDisabled && result.created.products > 0 && (
              <Alert variant="info" style={{ fontSize: "0.85rem", padding: "9px 13px" }}>
                All {result.created.products} new dishes were created{" "}
                <strong>deactivated</strong>. Check them in the menu drawer, then activate
                them from each card or select several and use Activate selected.
              </Alert>
            )}
            <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #e6ebf1", borderRadius: 6, padding: 10 }}>
              {result.log.map((entry, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: "0.8rem",
                    padding: "3px 0",
                    color:
                      entry.level === "error"
                        ? "#b3261e"
                        : entry.level === "warn"
                        ? "#a35b00"
                        : "#4a5c6e",
                  }}
                >
                  {entry.message}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === STEP.PICK && (
          <>
            <Button variant="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleValidate} disabled={!workbook || validating}>
              {validating ? <Spinner as="span" animation="border" size="sm" /> : "Check the file"}
            </Button>
          </>
        )}
        {step === STEP.REVIEW && (
          <>
            <Button variant="light" onClick={() => setStep(STEP.PICK)}>
              Back
            </Button>
            <Button
              variant="success"
              onClick={handleRun}
              disabled={!plan.canCommit || (totals.itemsToCreate === 0 && !(updateExisting && totals.itemsToUpdate > 0))}
            >
              <FaUpload style={{ marginRight: 6 }} />
              {updateExisting && totals.itemsToUpdate > 0
                ? `Import ${totals.itemsToCreate} new, update ${totals.itemsToUpdate}`
                : `Import ${totals.itemsToCreate} new item${totals.itemsToCreate === 1 ? "" : "s"}`}
            </Button>
          </>
        )}
        {step === STEP.RUNNING && (
          <Button variant="light" disabled>
            Importing...
          </Button>
        )}
        {step === STEP.DONE && (
          <Button variant="success" onClick={handleClose}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MenuImportModal;
